import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileLines,
  faFilePdf,
  faImage,
  faVideo,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";

import { MESSAGE_TYPE, splitLinks } from "@/components/weAllTeam/weAllTeamUtils";

const isPdf = (message) =>
  /pdf/i.test(message.mime_type || "") ||
  /\.pdf$/i.test(message.file_name || "");

/** Message text with every http(s)/www link clickable (opens in a new tab). */
export const LinkifiedText = ({ text }) =>
  splitLinks(text).map((part, index) =>
    part.href ? (
      <a
        key={index}
        href={part.href}
        target="_blank"
        rel="noopener noreferrer"
        className="weall-team-link"
      >
        {part.text}
      </a>
    ) : (
      <React.Fragment key={index}>{part.text}</React.Fragment>
    ),
  );

/**
 * What a chat bubble shows: the photo / video player / document card when the message carries a file, then the
 * text (or caption) with clickable links.
 */
const WeAllTeamMessageBody = ({ message }) => {
  const url = message.file_url;
  const type = message.message_type;
  const name = message.file_name || "";

  return (
    <>
      {url && type === MESSAGE_TYPE.IMAGE && (
        <a href={url} target="_blank" rel="noopener noreferrer">
          <img
            src={url}
            alt={name}
            className="weall-team-media-image"
            loading="lazy"
          />
        </a>
      )}
      {url && type === MESSAGE_TYPE.VIDEO && (
        <video
          className="weall-team-media-video"
          src={url}
          poster={message.thumbnail_url || undefined}
          controls
          preload="metadata"
        />
      )}
      {url && (type === MESSAGE_TYPE.DOCUMENT || type === MESSAGE_TYPE.FILE) && (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          download={name || true}
          className="weall-team-media-document"
        >
          <FontAwesomeIcon
            icon={isPdf(message) ? faFilePdf : faFileLines}
            className="weall-team-media-document-icon"
          />
          <span className="weall-team-media-document-name">
            {name || url.split("/").pop()}
          </span>
          <FontAwesomeIcon icon={faDownload} />
        </a>
      )}
      {message.message ? (
        <div className="weall-team-bubble-text">
          <LinkifiedText text={message.message} />
        </div>
      ) : null}
    </>
  );
};

/** Inbox "last message": text, or an icon + label for a photo / video / PDF / document. */
export const LastMessagePreview = ({ row, labels }) => {
  const type = row.last_message_type;
  const pdf = /\.pdf$/i.test(row.last_message_file_name || "");
  const media =
    type === MESSAGE_TYPE.IMAGE
      ? { icon: faImage, label: labels.photo }
      : type === MESSAGE_TYPE.VIDEO
        ? { icon: faVideo, label: labels.video }
        : type === MESSAGE_TYPE.DOCUMENT || type === MESSAGE_TYPE.FILE
          ? {
              icon: pdf ? faFilePdf : faFileLines,
              label: pdf ? labels.pdf : labels.document,
            }
          : null;
  if (!media) return <>{row.last_message}</>;
  return (
    <span className="weall-team-last-media">
      <FontAwesomeIcon icon={media.icon} className="me-1" />
      {media.label}
      {row.last_message ? `: ${row.last_message}` : ""}
    </span>
  );
};

export default WeAllTeamMessageBody;
